import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Container from '../components/Container';
import Button from '../components/Button';
import InfraProjectModal from '../components/InfraProjectModal';

/*
 * §7.3.2 — single wide project card + dots. Navigate via prev/next arrows, touch
 * swipe, or the mouse wheel (one project per scroll, with a cooldown, releasing
 * to page scroll at the first/last project). Each change animates in directionally.
 */
export default function InfraProjectSlider({ projects }) {
  const list = (projects || []).slice().sort((a, b) => a.order - b.order);
  const count = list.length;

  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [modal, setModal] = useState(null);
  const rootRef = useRef(null);
  const lockRef = useRef(false);
  const touchX = useRef(null);

  const go = useCallback(
    (target) => {
      setIndex((cur) => {
        const next = Math.max(0, Math.min(count - 1, target));
        setDir(next >= cur ? 1 : -1);
        return next;
      });
    },
    [count]
  );

  // Wheel → one step (with cooldown); release at the ends so the page scrolls.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      const delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (Math.abs(delta) < 8) return;
      const forward = delta > 0;
      if ((forward && index >= count - 1) || (!forward && index <= 0)) return; // let page scroll
      e.preventDefault();
      if (lockRef.current) return;
      lockRef.current = true;
      setTimeout(() => {
        lockRef.current = false;
      }, 520);
      go(index + (forward ? 1 : -1));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [index, count, go]);

  if (!count) return null;
  const p = list[index];

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) go(index + (dx < 0 ? 1 : -1));
    touchX.current = null;
  };

  return (
    <div className="infra-slider" ref={rootRef}>
      <Container>
        <div className="slider-stage">
          <button
            className="slider-arrow slider-arrow--prev"
            onClick={() => go(index - 1)}
            disabled={index === 0}
            aria-label="Previous project"
          >
            <ChevronLeft size={22} />
          </button>

          <article
            className="infra-card"
            key={index}
            data-dir={dir}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div className="infra-card__media">
              <img src={p.image} alt={p.title} />
            </div>
            <div className="infra-card__body">
              <p className="infra-card__date">{p.date}</p>
              <h3 className="infra-card__title">{p.title}</h3>
              <p className="infra-card__text">{p.body}</p>
              <Button variant="accent" onClick={() => setModal(p)} className="infra-card__btn">
                {p.readMoreLabel || 'READ MORE'}
              </Button>
            </div>
          </article>

          <button
            className="slider-arrow slider-arrow--next"
            onClick={() => go(index + 1)}
            disabled={index === count - 1}
            aria-label="Next project"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        <div className="slider-dots" role="tablist" aria-label="Project navigation">
          {list.map((proj, i) => (
            <button
              key={proj.id}
              className={`slider-dot${i === index ? ' is-active' : ''}`}
              onClick={() => go(i)}
              aria-label={`Go to project ${i + 1}`}
              aria-selected={i === index}
              role="tab"
            />
          ))}
        </div>
      </Container>

      {modal && <InfraProjectModal project={modal} onClose={() => setModal(null)} />}
    </div>
  );
}
